import type { Resolvers } from '@cogo/binder'
import type { App as AppManifest, BlockDef } from '@cogo/types-v2'

// Tunable picker weights — exposed for unit-test calibration (A3.1 from types.ts).
export const W_TAGS = 0.3
export const W_DS = 0.2
export const ONTOLOGY_CANDIDATE_QUALITY_FLOOR = 85

/**
 * The element a creator has picked, in the one shape every refinement consumer
 * takes. Its ONLY reader is the local-edit planner —
 * `features/chat/intent-chunks/intent-chunk-submit.ts:97` (`resolveElementTarget`),
 * reached via `submitIntentChunkPlan` from the two composers and from the
 * `ambiguous-target` retry in `chat-messages.tsx`.
 *
 * It is NOT part of `ChannelInput`, and must not be added back to it. A channel
 * dispatch replaces the whole project (`use-channel-dispatch.ts`
 * `applyRenderedOutputToProject` assigns `blocks: out.blocks` wholesale) from a
 * corpus example or a freshly generated block, so there is no point in that
 * pipeline where an element identity could change the outcome. It carried
 * `refinementContext?` from A3.4 to 2026-08-19 with one writer and zero readers.
 * `channel-router/__tests__/channel-input-fields-are-read.test.ts` holds the line.
 *
 * It lives in this file for its import graph (the composers and
 * `selection-context.ts` already reach here), not because the channels use it.
 */
export interface RefinementContext {
  element_id: string
  element_type: string
  current_state: Record<string, unknown>
  block_id: string
}

export type ChannelInput =
  | { kind: 'examplePick'; exampleId: string; resolverOverrides?: Partial<Resolvers> }
  | {
      kind: 'intent'
      pattern?: string
      semanticType?: string
      dsId?: string
      locale?: string
      k?: number
      resolverOverrides?: Partial<Resolvers>
    }

export interface RenderedTimings {
  lookupMs: number       // Channel 1 only (Channel 2 = 0)
  bindMs: number
  generationMs?: number  // Channel 2 only
  totalMs: number
}

export interface PageProgress {
  id: string
  name: string
  status: 'done' | 'in-progress' | 'pending'
  progress?: number  // 0..1, in-progress일 때만
}

export type RuntimeTargetSupport = 'supported' | 'partial' | 'unsupported'

export interface ComposeCandidateSummary {
  id: string
  semanticType: string
  patternRef: string
  intentMatch: number
  /**
   * Raw intent-token overlap behind `intentMatch`, when the source measured
   * one (the real backend's pick trace: matched-tokens / intent-tokens). The
   * card renders "intent 2/14" instead of a misleading "14%" — a token RATIO
   * is not a quality score, and dressing it as a percentage invited reading
   * it as one (2026-06-11 dogfood). Mock candidates omit this and keep the
   * percentage display.
   */
  intentMatchDetail?: { matched: number; total: number }
  qualityGrade?: number
  score: number
  runtimeTargets: Record<string, RuntimeTargetSupport>
  requiredState: string[]
  requiredActions: string[]
  requiredDatasources: string[]
  /**
   * `unbound` (real path only): the candidate was retrieved and measured but
   * NOT bound — only the picked winner gets bound, so its evidence is simply
   * not collected. Distinct from `missing`, which claims evidence was looked
   * for and absent (2026-06-11 dogfood: unpicked candidates read as alarming
   * "evidence missing").
   */
  evidenceState: 'complete' | 'partial' | 'missing' | 'unbound'
  status: 'ready' | 'blocked'
  blockReason?: string
}

export interface ComposePlanPreview {
  intent: string
  pageCount: number
  candidateCount: number
  readyCandidateCount: number
  blockedCandidateCount: number
  stateBindingCount: number
  actionBindingCount: number
  datasourceBindingCount: number
}

export interface ComposeBindPreview {
  state: string[]
  actions: string[]
  datasources: string[]
  warnings: string[]
}

/**
 * What actually produced a Channel 2 real-mode render. Mirrors the backend's
 * block `meta.generationSource`/`meta.exampleId` provenance contract
 * (cogo-platform review C3/D-2) — present only on real-mode renders whose
 * response carried it; mock renders never claim a generation source.
 */
export interface RenderedProvenance {
  generationSource: 'retrieval' | 'zero-shot'
  exampleId?: string
}

export type ChannelMissReason =
  | 'no-candidates'
  | 'no-template'
  | 'no-binding-schema'
  | 'kg-init-failed'
  | 'bind-throw'
  | 'wasm-unavailable'
  | 'aborted'
  | 'unsupported-mode'
  | 'backend-failed' // Channel 2 real-mode: HTTP / network / non-2xx without validation report

export type ChannelOutput =
  | {
      kind: 'rendered'
      channel: 1 | 2                         // NEW — A3.3 router의 chat msg variant
      exampleId?: string                     // Channel 1만, Channel 2는 undefined
      manifest: AppManifest
      blocks: Record<string, BlockDef>
      bindWarnings: string[]
      timings: RenderedTimings
      shapeSha: string
      // Channel 2 real-mode only — backend meta.generationSource/exampleId.
      provenance?: RenderedProvenance
    }
  | {
      kind: 'miss'
      reason: ChannelMissReason
      details?: string
      exampleId?: string
    }
  | {
      kind: 'streaming'
      pages: PageProgress[]                  // snapshot — 매번 full array 재전송
      elapsedMs: number
      totalDurationEstimateMs?: number
    }
  | {
      kind: 'compose-preview'
      plan: ComposePlanPreview
      candidates: ComposeCandidateSummary[]
      bindPreview: ComposeBindPreview
    }
  | {
      kind: 'shaclFail'
      failedPageId?: string
      diagnostics: string[]
      partialResult?: { manifest: AppManifest; blocks: Record<string, BlockDef> }
    }

export interface DispatchOneOpts {
  signal?: AbortSignal
  shapeSha?: string
}

export interface DispatchTwoOpts {
  signal?: AbortSignal
  shapeSha?: string
  mockMode?: 'success' | 'shaclFail' | 'miss' | 'timeout'
  totalDurationMs?: number    // default 30000
  pageCount?: number          // default 5
  chunkIntervalMs?: number    // default 1000

  /**
   * When true, dispatchChannelTwo bypasses the mock pipeline and calls the
   * real cogo-cloudflare backend at `backendUrl`. mockMode is ignored when
   * realMode is true.
   *
   * Phase 1 default: false (Channel 2 mock remains the default for tests
   * and `/__channel-2` dev playground). The /studio main route flips this
   * via store config when the user opts into real generation.
   */
  realMode?: boolean
  /** Required when realMode=true. Full URL of POST /api/v3/block/generate. */
  backendUrl?: string
  /** Real-mode pipeline; default 'v2' (no ontology). */
  realPipeline?: 'hybrid' | 'v2' | 'hybrid-with-fallback'
}
